/**
 * Type definitions for the anomaly detection pipeline (Anomalieerkennung).
 * Mirrors the backend `domains/adjustments` JSON contract.
 */

export type Lang = 'en' | 'de';

export type PipelineStatus =
  | 'idle'
  | 'uploading'
  | 'loaded'
  | 'configuring'
  | 'running'
  | 'awaiting_stl_threshold'
  | 'applying_stl'
  | 'awaiting_lstm_threshold'
  | 'applying_lstm'
  | 'complete'
  | 'error';

export interface PlotTrace {
  x: (string | number | null)[];
  y: (number | null)[];
  color?: string;
  label?: string | null;
  mode?: 'lines' | 'markers';
}

export interface PlotData {
  type: 'line' | 'scatter' | 'multi';
  traces: PlotTrace[];
  xaxis: { label: string; type: 'datetime' | 'numeric' };
  yaxis: { label: string };
  title: string;
  component?: string;
}

/**
 * Flat parameter shape sent to backend. Mirrors `build_par_dict` keys.
 * Numeric fields use `string` so empty inputs don't coerce to 0.
 */
export interface AnomalyParameters {
  eqMax: string;
  gapMax: string;
  dec: string;
  lgMin: string;
  vMax: string;
  vMin: string;
  el0: boolean;
  sbad: { chgMax: string; lgMax: string };
  stl: { run: boolean; periodH: string };
  lstm: {
    run: boolean;
    periodH: string;
    neurons: string;
    epochs: string;
    batchSize: string;
  };
}

export const ANOMALY_DEFAULTS: AnomalyParameters = {
  eqMax: '',
  gapMax: '',
  dec: '',
  lgMin: '',
  vMax: '',
  vMin: '',
  el0: false,
  sbad: { chgMax: '', lgMax: '' },
  stl: { run: false, periodH: '24' },
  lstm: { run: false, periodH: '24', neurons: '64', epochs: '20', batchSize: '16' },
};

export interface AnomalyPlots {
  original?: PlotData;
  slope?: PlotData;
  stlDecomposition?: PlotData[];
  stlAnomalies?: PlotData;
  lstmError?: PlotData;
  lstmAnomalies?: PlotData;
  processed?: PlotData;
}

export interface LoadResponse {
  plots: { original: PlotData; slope: PlotData };
  columnName: string;
  dtAvgH: number;
  /** Detected time step of the loaded file, in minutes. Absent on older backends. */
  timestepMin?: number;
  /** Detected offset of the loaded file, in minutes. Absent on older backends. */
  offsetMin?: number;
  rowCount: number;
  status: 'loaded';
}

export interface ValidateParamResponse {
  ok: boolean;
  error?: string;
}

/**
 * How much of the data STL actually scored. STL runs per contiguous valid
 * segment (each needs >= 2 periods); shorter segments are skipped. `warning`
 * is a backend-localized message, present only when `skippedSegments > 0`.
 * Additive contract — absent on older backends.
 */
export interface StlCoverage {
  analyzedSegments: number | null;
  skippedSegments: number | null;
  warning?: string;
}

export interface StartResponse {
  status: 'awaiting_stl_threshold' | 'awaiting_lstm_threshold' | 'complete';
  plots: Partial<AnomalyPlots>;
  sessionId: string;
  processedCsvFilename?: string | null;
  stlCoverage?: StlCoverage;
}

export interface ThresholdResponse {
  status: 'awaiting_lstm_threshold' | 'complete';
  plots: Partial<AnomalyPlots>;
  sessionId: string;
  processedCsvFilename?: string | null;
}

export interface UseProcessedResponse {
  status: 'loaded';
  plots: { original: PlotData; slope: PlotData };
  rowCount: number;
  sessionId: string;
}

/** Build the API request payload from local UI state. */
export function paramsToRequest(p: AnomalyParameters) {
  const num = (s: string) => (s === '' || s === null ? null : Number(s));
  return {
    eqMax: num(p.eqMax),
    gapMax: num(p.gapMax),
    dec: num(p.dec),
    lgMin: num(p.lgMin),
    vMax: num(p.vMax),
    vMin: num(p.vMin),
    el0: p.el0,
    sbad: { chgMax: num(p.sbad.chgMax), lgMax: num(p.sbad.lgMax) },
    stl: { run: p.stl.run, periodH: num(p.stl.periodH) },
    lstm: {
      run: p.lstm.run,
      periodH: num(p.lstm.periodH),
      neurons: num(p.lstm.neurons),
      epochs: num(p.lstm.epochs),
      batchSize: num(p.lstm.batchSize),
    },
  };
}
